import { DEFAULT_FEES, grossUpForFees, parseCents, type Cents, type GiveForm } from '@church/domain'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useApi } from '../api-context.tsx'
import { randomUUIDv7 } from '../offline/id.ts'

/**
 * The public Give page, as a flow: the form → the payment step → thank you.
 *
 * Every attempt to give carries a fresh Idempotency-Key generated *before*
 * the request, and a retry of the same attempt reuses it — so a double tap,
 * a flaky connection or an impatient refresh is one gift, not two.
 *
 * In test mode the payment step is a button the test processor answers; with
 * Stripe it is Stripe's own payment form, mounted by the page, so the card
 * number goes from the browser to Stripe and never through this code.
 */

export const giveKeys = {
  config: (slug: string) => ['give', slug] as const,
  intent: (slug: string, intentId: string) => ['give', slug, 'intent', intentId] as const,
}

export function useGiveConfig(slug: string) {
  const api = useApi()
  return useQuery({
    queryKey: giveKeys.config(slug),
    queryFn: async () => {
      const { data, error } = await api.api.v1.give({ slug }).get()
      if (error) throw error
      return data
    },
  })
}

export type GiveConfig = NonNullable<ReturnType<typeof useGiveConfig>['data']>

/** What will be charged for an amount typed, with or without the fee cover. */
export function previewCharge(
  amount: string,
  coverFees: boolean,
  method: 'card' | 'ach',
): Cents | null {
  const gift = parseCents(amount)
  if (gift === null || gift <= 0) return null
  return coverFees ? grossUpForFees(gift, DEFAULT_FEES[method]) : gift
}

export function useGiveFlow(slug: string) {
  const api = useApi()
  const [step, setStep] = useState<'form' | 'pay' | 'done'>('form')
  const [intent, setIntent] = useState<{
    intentId: string
    clientSecret: string
    chargeCents: number
    mode: string
  } | null>(null)
  // One key per attempt to give: created with the attempt, reused on retry.
  const [attemptKey, setAttemptKey] = useState(() => randomUUIDv7())

  const start = useMutation({
    mutationFn: async (form: GiveForm) => {
      const gift = parseCents(form.amount)
      if (gift === null || gift < 100) throw new Error('Give at least $1.00.')
      const { data, error } = await api.api.v1.give({ slug }).intents.post(
        {
          amountCents: gift,
          allocations: [{ fundId: form.fundId, amountCents: gift }],
          coverFees: form.coverFees,
          method: form.method,
          frequency: form.frequency === 'once' ? null : form.frequency,
          donor: { firstName: form.firstName, lastName: form.lastName, email: form.email },
        },
        { headers: { 'idempotency-key': attemptKey } },
      )
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      setIntent(data)
      setStep('pay')
    },
  })

  const confirmTest = useMutation({
    mutationFn: async () => {
      if (!intent) throw new Error('Nothing to pay')
      const { data, error } = await api.api.v1
        .give({ slug })
        .intents({ intentId: intent.intentId })
        ['test-confirm'].post({})
      if (error) throw error
      return data
    },
    onSuccess: () => setStep('done'),
  })

  return {
    step,
    intent,
    start,
    confirmTest,
    /** The processor confirmed in the browser (Stripe mode): show thanks. */
    markPaid: () => setStep('done'),
    giveAgain: () => {
      setIntent(null)
      setAttemptKey(randomUUIDv7())
      setStep('form')
    },
  }
}
